import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { formatFullName } from '@hub/common';
import { jwtDecode } from 'jwt-decode';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { ensureUserData } from 'features/auth/api/ensureUserData';
import { AuthErrorPage } from 'features/auth/components/AuthErrorPage';
import { SessionExpiryDialog } from 'features/auth/components/SessionExpiryDialog';
import { LOCAL_STORAGE_AUTH_LOGOUT } from 'features/auth/constants';
import { useActivityGatedTokenRenewal } from 'features/auth/hooks/useActivityGatedTokenRenewal';
import { useAutoSSO } from 'features/auth/hooks/useAutoSSO';
import { useIdleSignOut } from 'features/auth/hooks/useIdleSignOut';
import { selectOptionalUser } from 'features/auth/store/authSelectors';
import { setAuthState } from 'features/auth/store/authSlice';
import {
  isNoSessionRoute,
  isPageSessionStale,
  shouldRenderApp,
} from 'features/auth/utils/appRenderRule';
import { signinRedirectTarget } from 'features/auth/utils/authTarget';
import { classifyAuthErrorType } from 'features/auth/utils/classifyAuthError';
import { fetchWithTimeout } from 'features/auth/utils/fetchWithTimeout';
import { getTokenExchangeInstance } from 'features/auth/utils/getTokenExchangeInstance';
import { identityErrorCause } from 'features/auth/utils/identityErrorCause';
import {
  isStoredSessionStale,
  markStoredSessionStale,
  maxAgeSinceStale,
} from 'features/auth/utils/lastActivity';
import {
  decodeLogoutSignal,
  encodeLogoutSignal,
} from 'features/auth/utils/logoutSignal';
import { navigatorOutcome } from 'features/auth/utils/navigatorOutcome';
import { preloadSignedInCode } from 'features/auth/utils/preloadSignedInCode';
import { clearStateRecovery } from 'features/auth/utils/stateRecovery';
import { forgetBrandsButLast } from 'features/brand/brandCache';
import {
  resolveLoginBrandId,
  startLoginBrand,
} from 'features/brand/loginBrand';
import { useReportLoading } from 'features/loading';
import { logger } from 'features/logger/logger';
import { datadogRumAddCustomAction } from 'features/monitoring/customDatadogRumActions';
import {
  addPerformanceEndMark,
  addPerformanceStartMark,
} from 'features/monitoring/performanceMonitor';
import { RoutePath } from 'features/routing/constants';
import { useAppDispatch, useAppSelector } from 'features/store/hooks';
import { clearDatadogUser } from 'integrations/datadog/datadogUser';
import { initSatismeter } from 'integrations/satismeter/satismeter';
import {
  clearIntentionalNavigation,
  markIntentionalNavigation,
} from 'shared/utils/intentionalNavigationGuard';
import { logUnlessPageLeft } from 'shared/utils/pageLeave';
import { AuthContext } from './AuthContext';
import type { AuthContextValue, LogoutOptions } from './AuthContext';
import type { IdentityTokenUserSettings } from '@hub/common';
import type { AuthErrorType } from 'features/auth/types/authError';
import type { JwtPayload } from 'jwt-decode';
import type { SignoutRedirectArgs } from 'oidc-client-ts';
import type { ErrorContext } from 'react-oidc-context';

type AuthProviderProps = {
  children: ReactNode;
};

const loginRedirectErrorCause = (error: ErrorContext | undefined) =>
  error?.source === 'signinRedirect' ? identityErrorCause(error) : undefined;

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const dispatch = useAppDispatch();
  const identityAuth = useOidcAuth();
  const user = useAppSelector(selectOptionalUser);

  // logout reads the freshest identityAuth through this ref rather than
  // depending on its identity, which changes on every token-renewal dispatch;
  // that keeps logout, the cross-tab listener, and the context value stable.
  const identityAuthRef = useRef(identityAuth);
  useEffect(() => {
    identityAuthRef.current = identityAuth;
  });

  const [isResolvingAuth, setIsResolvingAuth] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // The state flag is committed by React; another tab's logout echo arrives
  // before that commit, so the guard against re-entry reads this ref.
  const loggingOutRef = useRef(false);
  const [firebaseSyncComplete, setFirebaseSyncComplete] = useState(false);
  const [authError, setAuthError] = useState<AuthErrorType | null>(null);

  // Track if auth flow has been completed for current session
  const authFlowCompleted = useRef(false);

  // Set once the stored user of a stale session is being removed
  const staleUserRemovalStarted = useRef(false);

  // Track signin redirect attempts to prevent infinite loops
  const signinRedirectAttempts = useRef(0);
  const loginRedirectPending = useRef(false);
  const MAX_SIGNIN_REDIRECT_ATTEMPTS = 10;

  const [staleAtLoad] = useState(isPageSessionStale);

  useEffect(() => {
    startLoginBrand(window.location.pathname);
  }, []);

  // Enable automatic SSO detection - checks for existing session from other apps
  const { ssoCheckComplete, forcedLoginFailed } = useAutoSSO(staleAtLoad);

  // The stored user could not be removed and the forced login redirect failed:
  // nothing else leaves the loading screen, so the error page offers a retry.
  useEffect(() => {
    if (!forcedLoginFailed || authError !== null) {
      return;
    }

    const cause = loginRedirectErrorCause(identityAuth.error);
    const errorType = classifyAuthErrorType(cause);
    logger.error(
      'Forced login redirect failed',
      { details: { errorType } },
      cause
    );
    setAuthError(errorType);
  }, [forcedLoginFailed, authError, identityAuth.error]);

  // Step 1: Exchange Identity token for Firebase token
  // Step 2: Sign into Firebase
  // Step 3: Update user details (Redux)
  const handlePostIdentityAuthenticationFlow = useCallback(
    async (identityUser: {
      profile: { sub: string };
      access_token: string;
    }) => {
      const userId = identityUser.profile.sub;

      // Skip if already completed
      if (authFlowCompleted.current) {
        logger.debug('Auth flow already completed for this session');
        // Still need to mark auth as resolved
        setIsResolvingAuth(false);
        return;
      }

      if (!identityUser.access_token) {
        logger.error('No access token available');
        return;
      }

      // Every logout marks the stored session stale first, so a stored user
      // that loads after another tab's logout never reaches the exchange: it
      // is removed and the login redirect asks for a newer sign-in. A sign-in
      // completing on the callback route has not yet overwritten the mark.
      if (isPageSessionStale()) {
        if (staleUserRemovalStarted.current) {
          return;
        }

        staleUserRemovalStarted.current = true;
        logger.info('Stored session is stale; removing the stored user');
        identityAuthRef.current.removeUser().then(
          () => {
            setIsResolvingAuth(false);
          },
          (error: unknown) => {
            // The reloaded page sees the stale stored session at load.
            logger.error(
              'Could not remove the stored user of a stale session',
              undefined,
              error as Error
            );
            window.location.reload();
          }
        );
        return;
      }

      preloadSignedInCode();

      try {
        addPerformanceStartMark('signIn');
        logger.debug('Starting authentication flow for user', {
          details: { userId },
        });

        // Step 1: Exchange Identity token for Firebase token
        logger.debug('Exchanging Identity token for Firebase token');
        const exchangeUrl = import.meta.env.PUBLIC_IDENTITY_TOKEN_EXCHANGE_URL;
        const firebaseInstance = getTokenExchangeInstance();
        const response = await fetchWithTimeout(
          exchangeUrl,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              jwtToken: identityUser.access_token,
              firebaseInstance,
            }),
          },
          10000 // 10 second timeout
        );

        if (!response.ok) {
          throw new Error(
            `Token exchange failed: ${String(response.status)} ${response.statusText}`
          );
        }

        const contentType = response.headers.get('content-type');
        if (!contentType?.includes('application/json')) {
          throw new Error(
            `Expected JSON response but got: ${contentType ?? 'unknown'}`
          );
        }

        const data = (await response.json()) as { firebaseToken: string };

        // Step 2: Sign into Firebase
        logger.debug('Signing into Firebase with custom token');
        await FirebaseAuthentication.signInWithCustomToken({
          token: data.firebaseToken,
        });

        // Step 3: Update user details
        logger.debug('Updating user details');
        const decodedToken = jwtDecode<JwtPayload & IdentityTokenUserSettings>(
          identityUser.access_token
        );

        const language = await ensureUserData({
          uid: userId,
          tokenLocale: decodedToken.locale,
        });

        // Initialize third-party services
        initSatismeter(userId);

        // Update Redux state with user details
        dispatch(
          setAuthState({
            user: {
              uid: userId,
              email: decodedToken.email,
              displayName: formatFullName(decodedToken) ?? null,
              language,
              role: decodedToken.realm_access?.roles?.some(
                role => role === 'castor_admin'
              )
                ? 'admin'
                : 'participant',
            },
          })
        );

        // Mark auth flow as completed and auth as resolved
        authFlowCompleted.current = true;
        signinRedirectAttempts.current = 0; // Reset counter on successful auth
        clearStateRecovery();
        setIsResolvingAuth(false);
        addPerformanceEndMark('signIn', { datadog: true });

        logger.debug('Authentication flow completed successfully');
      } catch (error) {
        // Reset auth flow to allow retry
        authFlowCompleted.current = false;

        logUnlessPageLeft('Authentication flow failed', error, () => {
          const errorType = classifyAuthErrorType(error);
          logger.error(
            'Auth error classified',
            { details: { errorType } },
            error as Error
          );
          setAuthError(errorType);

          // Mark auth as resolved to show error UI instead of spinner
          setIsResolvingAuth(false);
        });
      }
    },
    [dispatch]
  );

  // A failed login redirect resolves before React commits its error, so the
  // warn is logged once the error is in state.
  const [failedLoginRedirect, setFailedLoginRedirect] = useState<{
    reason: 'no-session' | 'stale-session';
    attempt: number;
  } | null>(null);

  useEffect(() => {
    if (failedLoginRedirect === null) {
      return;
    }

    const cause = loginRedirectErrorCause(identityAuth.error);
    logger.warn(
      'Login redirect failed',
      { details: { ...failedLoginRedirect, error: cause?.message } },
      cause
    );
    setFailedLoginRedirect(null);
  }, [failedLoginRedirect, identityAuth.error]);

  // LOGIN - Handle Identity authentication redirect
  useEffect(() => {
    // Don't redirect if logging out or auth still initializing
    if (isLoggingOut || isResolvingAuth) {
      return;
    }

    // The callback route finishes the sign-in itself; the signed-out page must
    // stay visible to a participant who was just signed out.
    // Note: We ignore error because SSO check may set login_required error
    if (
      authError === null &&
      !identityAuth.isAuthenticated &&
      !identityAuth.isLoading &&
      !identityAuth.activeNavigator &&
      !loginRedirectPending.current &&
      !isNoSessionRoute(window.location.pathname)
    ) {
      if (signinRedirectAttempts.current >= MAX_SIGNIN_REDIRECT_ATTEMPTS) {
        const cause = loginRedirectErrorCause(identityAuth.error);
        logger.error(
          `Max signin redirect attempts (${MAX_SIGNIN_REDIRECT_ATTEMPTS.toString()}) reached`,
          { details: { error: cause?.message } },
          cause
        );
        setAuthError('oidc-config');
        return;
      }

      signinRedirectAttempts.current += 1;
      loginRedirectPending.current = true;
      const attempt = signinRedirectAttempts.current;
      // A logout on this page marks the stored session stale, so a login
      // after its back/forward-cache restore needs a newer sign-in too.
      const stale = staleAtLoad || isStoredSessionStale();
      const reason = stale ? 'stale-session' : 'no-session';
      // A stale session accepts only an Identity sign-in made after it ended.
      const maxAge = stale ? maxAgeSinceStale() : undefined;
      const target = signinRedirectTarget();

      logger.info('Redirecting to Identity login', {
        details: { reason, attempt, maxAge },
      });
      markIntentionalNavigation();

      void resolveLoginBrandId(window.location.pathname)
        .then(brandId => {
          loginRedirectPending.current = false;
          return navigatorOutcome(
            identityAuth.signinRedirect({
              ...target,
              ...(maxAge !== undefined && { max_age: maxAge }),
              ...(brandId && { extraQueryParams: { brand: brandId } }),
            })
          );
        })
        .then(outcome => {
          if (attempt === signinRedirectAttempts.current) {
            clearIntentionalNavigation();
          }

          if (outcome === 'failed') {
            setFailedLoginRedirect({ reason, attempt });
          } else {
            logger.info('Login redirect returned to the page', {
              details: { reason, attempt },
            });
          }
        });
    }
  }, [identityAuth, isLoggingOut, isResolvingAuth, authError, staleAtLoad]);

  // handle post-Identity authentication
  useEffect(() => {
    // Skip if logging out or still loading
    if (
      isLoggingOut ||
      staleAtLoad ||
      identityAuth.isLoading ||
      identityAuth.activeNavigator
    ) {
      return;
    }

    // Handle authenticated state
    if (identityAuth.isAuthenticated && identityAuth.user) {
      // Type assertion needed due to react-oidc-context type limitations
      void handlePostIdentityAuthenticationFlow(identityAuth.user);
    }
    // For not authenticated state: Don't resolve auth here
    // Let the explicit SSO check effect handle auth resolution
    // This ensures SSO check completes before showing login screen
  }, [
    identityAuth.isAuthenticated,
    identityAuth.user,
    identityAuth.isLoading,
    identityAuth.activeNavigator,
    isLoggingOut,
    staleAtLoad,
    handlePostIdentityAuthenticationFlow,
  ]);

  // Explicit auth resolution for unauthenticated flow
  // CRITICAL: This is the only place that resolves auth when no user exists
  // Without this, login redirect would be blocked forever (checks isResolvingAuth)
  // Waits for both SSO check AND Firebase sync to prevent flash of content
  useEffect(() => {
    if (
      ssoCheckComplete &&
      firebaseSyncComplete &&
      !identityAuth.isAuthenticated &&
      isResolvingAuth
    ) {
      logger.debug(
        'SSO check and Firebase sync complete with no user, resolving auth'
      );
      setIsResolvingAuth(false);
    }
  }, [
    ssoCheckComplete,
    firebaseSyncComplete,
    identityAuth.isAuthenticated,
    isResolvingAuth,
  ]);

  // Sync Firebase with Identity - if Identity is not authenticated, sign out from Firebase
  // This happens when a user logs out from Identity in other our service (eg eConsent)
  useEffect(() => {
    const syncAuth = async () => {
      // Only proceed if Identity has finished loading and is not authenticated
      // Check Firebase auth state directly instead of using state variable
      const { user: firebaseUser } =
        await FirebaseAuthentication.getCurrentUser();

      if (
        ssoCheckComplete &&
        !identityAuth.isLoading &&
        !identityAuth.isAuthenticated &&
        !identityAuth.activeNavigator &&
        firebaseUser &&
        !isLoggingOut
      ) {
        logger.debug(
          'Identity not authenticated but Firebase user exists, signing out from Firebase to sync'
        );

        // Sign out from Firebase to match Identity's state
        try {
          await FirebaseAuthentication.signOut();
        } catch (error) {
          logger.error(
            'Failed to sign out from Firebase during sync',
            undefined,
            error as Error
          );
        }
      }

      // Mark sync as complete (whether signout happened or not)
      setFirebaseSyncComplete(true);
    };

    void syncAuth();
  }, [
    identityAuth.isLoading,
    identityAuth.isAuthenticated,
    identityAuth.activeNavigator,
    isLoggingOut,
    ssoCheckComplete,
  ]);

  // Setup Firebase auth state listener
  useEffect(() => {
    const setupFirebaseAuthListener = async () => {
      try {
        await FirebaseAuthentication.removeAllListeners();

        await FirebaseAuthentication.addListener(
          'authStateChange',
          ({ user: authUser }) => {
            if (!authUser) {
              logger.debug(
                'Firebase auth state: initial load or user signed out'
              );
              dispatch(
                setAuthState({
                  user: undefined,
                })
              );
              // Reset auth flow tracking
              authFlowCompleted.current = false;
              return;
            }

            logger.debug('Firebase user authenticated');
          }
        );

        logger.debug('Firebase auth listener setup complete');
      } catch (error) {
        logger.error(
          'Failed to setup Firebase auth listener',
          undefined,
          error as Error
        );
      }
    };

    void setupFirebaseAuthListener();
  }, [dispatch]);

  // Set to the idle timer's stop once the timer hook runs below.
  const stopIdleTracking = useRef<() => void>(() => undefined);

  // Logout method
  const logout = useCallback(async (options?: LogoutOptions) => {
    if (loggingOutRef.current) {
      logger.debug('Logout already in progress');
      return;
    }

    loggingOutRef.current = true;
    setIsLoggingOut(true);
    logger.debug('Starting logout process');

    // Whether or not Identity ends its session, every later page load asks
    // Identity for a sign-in made after this logout until a sign-in
    // completes; no input on this page may record activity over the mark.
    stopIdleTracking.current();
    markStoredSessionStale();

    const reason = options?.reason ?? 'user';

    // Capture this tab's id token before any tab clears the shared user
    // store, so the end-session request keeps its id_token_hint and Identity
    // skips its confirmation page.
    const idTokenHint = identityAuthRef.current.user?.id_token;

    // Reset auth flow tracking
    authFlowCompleted.current = false;
    signinRedirectAttempts.current = 0;

    // Clear Datadog user immediately
    datadogRumAddCustomAction({ name: 'userLogout' });
    clearDatadogUser();
    forgetBrandsButLast();

    // Signal other tabs to log out (replaces monitorSession cross-tab sync)
    localStorage.setItem(LOCAL_STORAGE_AUTH_LOGOUT, encodeLogoutSignal(reason));
    markIntentionalNavigation();

    try {
      logger.debug('Signing out from Firebase');
      await FirebaseAuthentication.signOut();
      logger.debug('Firebase signout complete');
    } catch (error) {
      logger.warn(
        'Firebase signout error, proceeding with Identity logout',
        undefined,
        error as Error
      );
    }

    // Identity already ended the session: an end-session request would name
    // a session that is gone, and Identity asks to confirm it when the browser
    // holds a newer session. The login redirect takes over instead.
    if (reason === 'session-ended') {
      try {
        await identityAuthRef.current.removeUser();
      } catch (error) {
        // The stored user would keep Identity authenticated on this page; the
        // reloaded page sees the stale stored session and asks for a newer
        // sign-in.
        logger.error(
          'Could not remove the stored user after Identity ended the session',
          undefined,
          error as Error
        );
        window.location.reload();
        return;
      }

      logger.debug('Identity session already ended; redirecting to login');
      clearIntentionalNavigation();
      loggingOutRef.current = false;
      setIsLoggingOut(false);
      return;
    }

    // An idle sign-out lands on the signed-out page, in this tab and in
    // every tab that follows it.
    const signoutArgs: SignoutRedirectArgs = {};

    if (reason === 'idle') {
      signoutArgs.post_logout_redirect_uri = `${window.location.origin}${RoutePath.SignedOut}`;
    }

    if (idTokenHint !== undefined) {
      signoutArgs.id_token_hint = idTokenHint;
    }

    logger.debug('Redirecting to Identity logout');
    const outcome = await navigatorOutcome(
      identityAuthRef.current.signoutRedirect(signoutArgs)
    );

    // Identity may still hold the session. The reloaded page sees the stale
    // stored session and asks for a sign-in made after this logout.
    if (outcome === 'failed') {
      logger.error('Identity logout redirect failed', { details: { reason } });
      window.location.reload();
      return;
    }

    logger.info('Identity logout redirect returned to the page', {
      details: { reason },
    });
    clearIntentionalNavigation();
    loggingOutRef.current = false;
    setIsLoggingOut(false);
  }, []);

  const signOutForIdleTimeout = useCallback(() => {
    logger.debug('Idle limit reached, signing out');
    datadogRumAddCustomAction({ name: 'userIdleTimeout' });
    void logout({ reason: 'idle' });
  }, [logout]);

  const {
    start: startIdleTimer,
    stop: stopIdleTimer,
    touch: stayActive,
    isIdle,
    warning: sessionExpiryWarning,
    remainingMs,
  } = useIdleSignOut({ onTimeout: signOutForIdleTimeout });

  useEffect(() => {
    stopIdleTracking.current = stopIdleTimer;
  }, [stopIdleTimer]);

  const [idleTimerStarted, setIdleTimerStarted] = useState(false);

  // The timer starts once, when the Redux user is set at the end of the auth flow.
  useEffect(() => {
    if (idleTimerStarted || user === undefined) {
      return;
    }

    startIdleTimer();
    setIdleTimerStarted(true);
  }, [idleTimerStarted, user, startIdleTimer]);

  const logOutAfterRejectedRenewal = useCallback(() => {
    void logout({ reason: 'session-ended' });
  }, [logout]);

  useActivityGatedTokenRenewal(
    identityAuth,
    isIdle,
    idleTimerStarted,
    logOutAfterRejectedRenewal
  );

  // Cross-tab logout runs through the auth_logout signal; monitorSession is off.
  // The 'storage' event fires only in OTHER tabs when localStorage changes.
  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (
        event.key !== LOCAL_STORAGE_AUTH_LOGOUT ||
        !event.newValue ||
        loggingOutRef.current
      ) {
        return;
      }

      const reason = decodeLogoutSignal(event.newValue);

      if (reason === null) {
        return;
      }

      if (!identityAuthRef.current.user) {
        logger.debug('Logout detected from another tab; no session to end');
        return;
      }

      logger.debug('Logout detected from another tab via storage event');
      void logout({ reason });
    };

    window.addEventListener('storage', handleStorageEvent);
    return () => window.removeEventListener('storage', handleStorageEvent);
  }, [logout]);

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      logout,
    }),
    [logout]
  );

  const renderApp = shouldRenderApp({
    isAuthenticated: identityAuth.isAuthenticated,
    pathname: window.location.pathname,
    isLoggingOut,
  });

  useReportLoading((isResolvingAuth || !renderApp) && authError === null);

  // Show error page if auth failed
  if (authError) {
    return <AuthErrorPage errorType={authError} />;
  }

  if (isResolvingAuth || !renderApp) {
    return null;
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <SessionExpiryDialog
        open={sessionExpiryWarning}
        remainingMs={remainingMs}
        onStay={stayActive}
      />
    </AuthContext.Provider>
  );
};
